import React, { FC } from 'react'
import { useFragment, graphql } from 'react-relay'
import { ImageBackground } from 'react-native'

import { Navigate } from 'react-router-dom'

import {
  Breakpoint,
  Element,
  ImageKitImage,
  Link,
  LinkText,
  Screen,
  Stack,
  Text,
  useBreakpoint,
  UserPrimaryPhoto,
  Card,
} from '@components'

import { HomeScreenGuildsList } from './HomeScreenGuildsList'
import { HomeScreenEventsList } from './HomeScreenEventsList'
import { HomeScreenPresentationsList } from './HomeScreenPresentationsList'

import { HERO_BACKGROUND_IMAGE_URL } from '@data/config/images'

import {
  AuthenticatedHomeScreen_query,
  AuthenticatedHomeScreen_query$key,
} from './__generated__/AuthenticatedHomeScreen_query.graphql'

export const AuthenticatedHomeScreen: FC<{
  query: AuthenticatedHomeScreen_query$key
}> = ({ query }) => {
  const data = useFragment(
    graphql`
      fragment AuthenticatedHomeScreen_query on Query {
        viewer {
          firstName
          lastName
          ...UserPrimaryPhoto_user
        }

        ...HomeScreenGuildsList_query
        ...HomeScreenEventsList_query
        ...HomeScreenPresentationsList_query
      }
    `,
    query
  )

  const breakpoint = useBreakpoint()

  if (!data.viewer) {
    return <Navigate to='/' />
  }

  return (
    <>
      <ImageKitImage
        as={ImageBackground}
        imageUrl={HERO_BACKGROUND_IMAGE_URL}
        maxHeight={100}
        maxWidth='max'
        transformations={{ c: 'force' }}
        variant='contentCentered'
      >
        <Stack alignItems='center' justifyContent='center'>
          {breakpoint === 0 ? (
            <Card variant='authenticatedHomeScreen' gap={4}>
              <Stack alignItems='center'>
                <UserPrimaryPhoto size={20} user={data.viewer} />
              </Stack>

              <AuthenticatedHomeScreenWelcomeText data={data} />
            </Card>
          ) : (
            <Element component='HomeScreenWelcome'>
              <Stack direction='horizontal' gap={8}>
                <UserPrimaryPhoto size={20} user={data.viewer} />

                <AuthenticatedHomeScreenWelcomeText data={data} />
              </Stack>
            </Element>
          )}
        </Stack>
      </ImageKitImage>

      <Screen variant='authenticatedHomeScreen'>
        <Stack gap={breakpoint === 0 ? 24 : 50}>
          <Stack gap={8}>
            <Stack gap={5}>
              <Stack gap={4}>
                <Breakpoint display={0}>
                  <Text size={9} variant='hero'>
                    Join Guilds
                  </Text>
                </Breakpoint>

                <Breakpoint display={[1, 2, 3, 4]}>
                  <Stack direction='horizontal' alignItems='center'>
                    <Stack direction='horizontal' expand>
                      <Text size={9} variant='hero'>
                        Join Guilds
                      </Text>
                    </Stack>

                    <Link to='/guilds'>
                      <LinkText variant='subHeading'>
                        {'Explore more Guilds ->'}
                      </LinkText>
                    </Link>
                  </Stack>
                </Breakpoint>

                <Text>Guilds are the home for your community.</Text>
              </Stack>

              <HomeScreenGuildsList query={data} />
            </Stack>

            <Breakpoint display={0}>
              <Stack direction='horizontal' justifyContent='flex-end' expand>
                <Link to='/guilds'>
                  <LinkText variant='subHeading'>
                    {'Explore more Guilds ->'}
                  </LinkText>
                </Link>
              </Stack>
            </Breakpoint>
          </Stack>

          <Stack gap={8}>
            <Stack gap={5}>
              <Stack gap={4}>
                <Breakpoint display={0}>
                  <Text size={9} variant='hero'>
                    Attend Events
                  </Text>
                </Breakpoint>

                <Breakpoint display={[1, 2, 3, 4]}>
                  <Stack direction='horizontal' alignItems='center'>
                    <Stack direction='horizontal' expand>
                      <Text size={9} variant='hero'>
                        Attend Events
                      </Text>
                    </Stack>

                    <Link to='/events'>
                      <LinkText variant='subHeading'>
                        {'Explore more Events ->'}
                      </LinkText>
                    </Link>
                  </Stack>
                </Breakpoint>

                <Text>
                  Events are where you can connect with the community.
                </Text>
              </Stack>

              <HomeScreenEventsList query={data} />
            </Stack>

            <Breakpoint display={0}>
              <Stack direction='horizontal' justifyContent='flex-end' expand>
                <Link to='/events'>
                  <LinkText variant='subHeading'>
                    {'Explore more Events ->'}
                  </LinkText>
                </Link>
              </Stack>
            </Breakpoint>
          </Stack>

          <Stack gap={8}>
            <Stack gap={5}>
              <Stack gap={4}>
                <Breakpoint display={0}>
                  <Text size={9} variant='hero'>
                    Watch Presentations
                  </Text>
                </Breakpoint>

                <Breakpoint display={[1, 2, 3, 4]}>
                  <Stack direction='horizontal' alignItems='center'>
                    <Stack direction='horizontal' expand>
                      <Text size={9} variant='hero'>
                        Watch Presentations
                      </Text>
                    </Stack>

                    <Link to='/presentations'>
                      <LinkText variant='subHeading'>
                        {'Explore more Presentations ->'}
                      </LinkText>
                    </Link>
                  </Stack>
                </Breakpoint>

                <Text>
                  Missed an Event? Catch the Presentations at any time!
                </Text>
              </Stack>

              <HomeScreenPresentationsList query={data} />
            </Stack>

            <Breakpoint display={0}>
              <Stack direction='horizontal' justifyContent='flex-end' expand>
                <Link to='/presentations'>
                  <LinkText variant='subHeading'>
                    {'Explore more Presentations ->'}
                  </LinkText>
                </Link>
              </Stack>
            </Breakpoint>
          </Stack>

          <Stack />
        </Stack>
      </Screen>
    </>
  )
}

const AuthenticatedHomeScreenWelcomeText: FC<{
  data: AuthenticatedHomeScreen_query
}> = ({ data }) => {
  if (!data.viewer) {
    return null
  }

  return (
    <Stack gap={2}>
      <Text size={6} variant='hero'>
        Hey {data.viewer?.firstName}!
      </Text>
      <Stack>
        <Element component='HomeScreenWelcomeText'>
          <Text variant='label'>
            We've selected a few Guilds, Events, and Presentations to get you
            started. See you around!
          </Text>
        </Element>
      </Stack>
    </Stack>
  )
}
